import {
    Box,
    Button,
    Grid,
    Skeleton,
    Theme,
    Typography,
    useTheme
} from "@mui/material"
import { IndNavBtnProps } from "./types"
import CustomTable from "../common/customTable"
import { FilterIcon } from "../../../assets/SvgIcons"

export const InfoCardLoading = (): JSX.Element => {
    return (
        <>
            <Skeleton
                animation="wave"
                sx={{ width: '60%' }}
            />
            <Skeleton
                animation="wave"
                sx={{ width: '100%' }}
            />
        </>
    )
}

export const PieChartComponentLoading = ({ title }: { title: string | undefined }): JSX.Element => {
    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}
            >
                <Typography
                    variant="h2"
                    sx={(theme: Theme) => ({
                        lineHeight: "25px",
                        letterSpacing: "0.01px",
                        color: theme.palette.text.primary
                    })}
                >
                    {title || "Asset Allocation"}
                </Typography>
                <Skeleton
                    animation='wave'
                    width={200}
                    height={30}
                />
            </Box>
            <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                flexWrap: { xs: 'wrap', lg: 'nowrap' },
            }}>
                <Skeleton
                    variant="circular"
                    height={200}
                    sx={{
                        margin: "35px",
                        width: '100%',
                        maxWidth: '200px',
                    }} />
            </Box>
            <Grid
                container
                sx={{ paddingRight: "10px" }}
                rowSpacing={3}
            >
                {[...Array(5)].map((_, index) => (
                    <>
                        <Grid
                            key={index}
                            xs={6}
                            item
                            sx={{
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "center",
                                columnGap: "15px",
                            }}
                        >
                            <Skeleton
                                variant="circular"
                                width={11}
                                height={11}
                                animation="wave"
                            />
                            <Skeleton
                                sx={{ fontSize: '16px' }}
                                animation="wave"
                                width={100}
                            />
                        </Grid>
                        <Grid
                            xs={6}
                            item
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center",
                                columnGap: "15px"
                            }}
                        >
                            <Skeleton
                                sx={{ fontSize: '16px' }}
                                animation="wave"
                                width={100}
                            />
                        </Grid>
                    </>
                ))}
            </Grid>
        </>
    )
}

const navChartButtons = [{
    type: 'month',
    count: 6,
    text: '6M'
}, {
    type: 'year',
    count: 1,
    text: '1Y'
},
{
    type: 'all',
    text: 'Inception'
}]

export const PerformanceLoading = (): JSX.Element => {
    return (
        <>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                rowGap: '10px',
                mb: '55px'
            }}>
                <Typography
                    variant="h2"
                    color='text.primary'
                >
                    Portfolio Performance
                </Typography>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: { sm: 'flex-end', md: 'flex-start' },
                    columnGap: 3,
                }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            columnGap: '7px'
                        }}>

                        <Skeleton
                            animation="wave"
                            sx={{ fontSize: '16px' }}
                            width="5rem"
                        />
                        <Skeleton
                            variant="circular"
                            width={11}
                            height={11}
                            animation="wave"
                        />
                    </Box>
                    {/* <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            columnGap: '7px'
                        }}
                    >
                        <Skeleton
                            variant="circular"
                            width={11}
                            height={11}
                            animation="wave"
                        />
                        <Skeleton
                            animation="wave"
                            sx={{ fontSize: '16px' }}
                            width="5rem"
                        />
                    </Box> */}
                </Box>
            </Box>
            <Box>
                <Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Skeleton variant="rectangular" width={10} height={130} />
                        <Skeleton variant="rectangular" width={10} height={130} />
                        <Skeleton variant="rectangular" width={10} height={130} />
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                        <Skeleton variant="rectangular" width='100%' height={10} />
                    </Box>
                </Box>

                <Box
                    sx={{
                        mt: '45px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        columnGap: '10px'
                    }}
                >
                    {navChartButtons?.map((individualBtn: IndNavBtnProps, index: number) => {
                        return (
                            <Button
                                key={index}
                                sx={(theme: Theme) => ({
                                    ...theme.typography.body_500,
                                    letterSpacing: '0.02px',
                                    borderRadius: '7px',
                                    padding: '5px 15px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: { color: "Inception" === individualBtn?.text ? theme.palette.common.white : theme.palette.text.secondary },
                                    '&:hover': {
                                        border: 'none',
                                    }
                                }
                                )}
                                variant={"Inception" === individualBtn?.text ? 'contained' : 'outlined'}
                                disabled={true}
                            >
                                {individualBtn?.text}
                            </Button>
                        )
                    })}
                </Box>
            </Box>
        </>
    )
}

export const TopFivePerformersLoading = (): JSX.Element => {
    return (
        <>
            <Typography
                variant="h2"
                sx={(theme: Theme) => ({
                    lineHeight: "25px",
                    letterSpacing: "0.01px",
                    color: theme.palette.text.primary
                })}
            >
                Top 5 Performers
            </Typography>
            <Box sx={{ marginTop: "20px" }}>
                <CustomTable
                    isLoading={true}
                    tableHeaderData={[...Array(5)]}
                    tableBodyLoaderRowCustomStyles={{
                        '& td:first-of-type': {
                            paddingLeft: "0px"
                        },
                    }}
                    tableHeadRowCustomStyles={{
                        '& th:first-of-type': {
                            paddingLeft: "0px"
                        }
                    }}
                    loadingRowsNum={5}
                />
            </Box>
        </>
    )
}

export const BottomFivePerformersLoading = (): JSX.Element => {
    return (
        <>
            <Typography
                variant="h2"
                sx={
                    (theme: Theme) => ({
                        lineHeight: "25px",
                        letterSpacing: "0.01px",
                        color: theme.palette.text.primary
                    })
                }
            >
                Bottom 5 Performers
            </Typography>
            <Box sx={{ marginTop: "20px" }}>
                <CustomTable
                    isLoading={true}
                    tableHeaderData={[...Array(5)]}
                    tableBodyLoaderRowCustomStyles={{
                        '& td:first-of-type': {
                            paddingLeft: "0px"
                        },
                    }}
                    tableHeadRowCustomStyles={{
                        '& th:first-of-type': {
                            paddingLeft: "0px"
                        }
                    }}
                    loadingRowsNum={5}
                />
            </Box>
        </>
    )
}

export const HoldingDetailsLoading = (): JSX.Element => {
    const theme = useTheme()

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between"
                }}
            >
                <Typography
                    variant="h2"
                    sx={(theme: Theme) => ({
                        lineHeight: "25px",
                        letterSpacing: "0.01px",
                        color: theme.palette.text.primary
                    })}
                >
                    Holdings
                </Typography>
                <Button
                    variant="text"
                    startIcon={<Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            opacity: 0.5
                        }}
                    >
                        <FilterIcon />
                    </Box>
                    }
                    sx={(theme: Theme) => ({
                        color: theme.palette.text.primary,
                        padding: "5px",
                        ...theme.typography.body_500,
                        "&:hover": {
                            backgroundColor: theme.palette.grey[50]
                        }
                    })}
                    TouchRippleProps={{
                        style: {
                            color: theme.palette.grey[300]
                        }
                    }}
                    disabled={true}
                >
                    Filters
                </Button>
            </Box>
            <Box
                sx={{
                    marginTop: "20px",
                    overflowX: "auto",
                    width: "100%",
                }}
            >
                <CustomTable
                    isLoading={true}
                    tableHeaderData={[...Array(6)]}
                    tableContainerStyles={{
                        paddingBottom: "20px"
                    }}
                    tableBodyLoaderRowCustomStyles={{
                        '& td:first-of-type': {
                            paddingLeft: "0px"
                        },
                    }}
                    tableHeadRowCustomStyles={{
                        '& th:first-of-type': {
                            paddingLeft: "0px"
                        }
                    }}
                    loadingRowsNum={5}
                />
            </Box>
        </>
    )
}

